import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../../ui/badge';
import { ValueSetCombobox, type ValueSetCode } from '../ValueSetCombobox';
import {
  freeTextItem,
  isFreeTextItem,
  MIN_QUERY_LENGTH,
  toDrugSearchSelection,
  withFreeTextItem,
  type DrugSearchSelection,
} from './drugSearchSelection';
import type { PrescribableMedication, SearchMedicationsQueryOptionsFactory } from './types';

/** The `designation.use.code` we stamp the parent generic name under, so the dropdown row renderer
 * can pull it back off the `ValueSetCode` (the primitive only threads `ValueSetCode`, not the raw
 * search row, into the renderer). */
const GENERIC_NAME_USE = 'generic-name';

/** Render one drug row: the name, a brand/generic pill on the right, and — for a brand — the parent
 * generic name below (e.g. "metformin HCl" under "Glucophage"). The free-text row renders as an
 * "Add … as free text" action with its own pill. */
function renderDrugRow(item: ValueSetCode, t: TFunction<'records'>): React.ReactNode {
  if (isFreeTextItem(item)) {
    return (
      <div className="flex w-full items-start justify-between gap-2" data-testid="free-text-medication-option">
        <p className="min-w-0 truncate">{t('addFreeTextMedication', { name: item.display })}</p>
        <Badge variant="outline" className="mt-0.5 shrink-0">
          {t('freeText')}
        </Badge>
      </div>
    );
  }
  const brandOrGeneric = item.designation?.find((d) => d.use?.code === 'brand-or-generic')?.value;
  const genericName = item.designation?.find((d) => d.use?.code === GENERIC_NAME_USE)?.value;
  return (
    <div className="flex w-full items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="truncate">{item.display ?? item.code}</p>
        {genericName && <p className="truncate text-xs text-muted-foreground">{genericName}</p>}
      </div>
      {brandOrGeneric && (
        <Badge variant="secondary" className="mt-0.5">
          {brandOrGeneric === 'brand' ? t('brand') : t('generic')}
        </Badge>
      )}
    </div>
  );
}

/** Map a search row to a `ValueSetCode`, stashing brand/generic + the parent generic name as
 * designations so `renderDrugRow` can render the pill + subtitle. `display` stays the plain name so
 * the selected value's input text is clean. */
function toDrugItem(m: PrescribableMedication & { routedMedId: string }): ValueSetCode {
  const designation = [
    ...(m.brandOrGeneric ? [{ use: { code: 'brand-or-generic' }, value: m.brandOrGeneric }] : []),
    ...(m.genericName ? [{ use: { code: GENERIC_NAME_USE }, value: m.genericName }] : []),
  ];
  return { code: m.routedMedId, display: m.name, ...(designation.length > 0 ? { designation } : {}) };
}

export interface DrugSearchFieldProps {
  /** The current selection — a catalog drug row or a free-text entry — or null. */
  value: DrugSearchSelection | null;
  /** Fires when the user picks (or clears) a selection — the parent drills a drug into its
   * formulations; a free-text pick goes straight to manual dosage entry. */
  onChange: (selection: DrugSearchSelection | null) => void;
  /** Pass-through to the app's `erx.searchMedications` query options. */
  searchMedications: SearchMedicationsQueryOptionsFactory;
}

/** Step 1 of the medication picker: a ScriptSure drug-name type-ahead ({ term } search), with a
 * trailing "add as free text" row for medications the catalog doesn't carry. Emits the pick up;
 * the parent runs the formulation lookup for a drug row. */
export function DrugSearchField({ value, onChange, searchMedications }: DrugSearchFieldProps): React.ReactNode {
  const { t } = useTranslation('records');
  const [filter, setFilter] = useState('');

  const query = useQuery({
    ...searchMedications({ term: filter }),
    enabled: filter.trim().length >= MIN_QUERY_LENGTH,
  });
  const rows = (query.data ?? []) as PrescribableMedication[];

  const items: ValueSetCode[] = useMemo(
    () =>
      withFreeTextItem(
        rows
          .filter((m): m is PrescribableMedication & { routedMedId: string } => Boolean(m.routedMedId))
          .map(toDrugItem),
        filter,
        { isFetching: query.isFetching, isError: query.isError },
      ),
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- `rows` is derived from query.data
    [query.data, query.isFetching, query.isError, filter],
  );

  const handleChange = (selected: ValueSetCode | null): void => {
    onChange(toDrugSearchSelection(selected, rows));
  };

  const comboValue = useMemo<ValueSetCode | null>(() => {
    if (value?.kind === 'freeText') return freeTextItem(value.name);
    if (value?.kind === 'drug' && value.drug.routedMedId) {
      return { code: value.drug.routedMedId, display: value.drug.name };
    }
    return null;
  }, [value]);
  const error = query.error instanceof Error ? query.error.message : undefined;
  const emptyMessage = filter.trim().length < MIN_QUERY_LENGTH ? t('typeToSearchMedications') : t('noMedicationsFound');

  return (
    <ValueSetCombobox
      id="med-drug"
      label={t('medicationOrSupplement')}
      items={items}
      value={comboValue}
      onValueChange={handleChange}
      onQueryChange={setFilter}
      isLoading={query.isFetching}
      error={error}
      emptyMessage={emptyMessage}
      renderItem={(item): React.ReactNode => renderDrugRow(item, t)}
    />
  );
}
